import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { getVacations, Vacation } from '../../services/api';
import { logger } from '../../utils/logger';
import VacationModal from './VacationModal';
import './CalendarSection.css';

// Default values for pre-filling vacation form
export interface VacationDefaults {
    title?: string;
    startDate?: string;
    endDate?: string;
    itineraryId?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================
export default function CalendarSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [year, setYear] = useState(new Date().getFullYear());
    const [vacations, setVacations] = useState<Vacation[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingVacation, setEditingVacation] = useState<Vacation | null>(null);
    const [defaultValues, setDefaultValues] = useState<VacationDefaults | null>(null);

    // Check URL params on mount for add-vacation action
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'add-vacation') {
            const defaults: VacationDefaults = {};
            if (params.get('tripId')) defaults.itineraryId = params.get('tripId')!;
            if (params.get('title')) defaults.title = params.get('title')!;
            if (params.get('start')) defaults.startDate = params.get('start')!;
            if (params.get('end')) defaults.endDate = params.get('end')!;

            setDefaultValues(defaults);
            setEditingVacation(null);
            setShowModal(true);

            // Clean URL params
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    const fetchVacations = async () => {
        setLoading(true);
        try {
            const { vacations: data } = await getVacations(year);
            setVacations(data);
        } catch (err) {
            logger.error('Failed to load vacations:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVacations();
    }, [year]);

    const handleAddClick = () => {
        setDefaultValues(null);
        setEditingVacation(null);
        setShowModal(true);
    };

    const handleVacationClick = (vacation: Vacation) => {
        setDefaultValues(null);
        setEditingVacation(vacation);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingVacation(null);
        setDefaultValues(null);
    };

    const handleSaved = () => {
        setShowModal(false);
        setEditingVacation(null);
        fetchVacations();
    };

    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const getVacationsForMonth = (monthIndex: number) => {
        const monthStart = new Date(Date.UTC(year, monthIndex, 1));
        const monthEnd = new Date(Date.UTC(year, monthIndex + 1, 0));

        return vacations.filter(v => {
            const start = new Date(v.startDate);
            const end = new Date(v.endDate);
            return start <= monthEnd && end >= monthStart;
        });
    };

    const getTypeLabel = (type: Vacation['type']) => {
        const labels: Record<Vacation['type'], string> = {
            vacation: t('calendar.type_vacation'),
            city_break: t('calendar.type_city_break'),
            road_trip: t('calendar.type_road_trip'),
            workation: t('calendar.type_workation'),
        };
        return labels[type];
    };

    const getStatusLabel = (status: Vacation['status']) => {
        const labels: Record<Vacation['status'], string> = {
            planned: t('calendar.status_planned'),
            confirmed: t('calendar.status_confirmed'),
            finished: t('calendar.status_finished'),
        };
        return labels[status];
    };

    return (
        <div className="calendar-section">
            {/* Header with Year Selector and Add Button */}
            <div className="calendar-header">
                <div className="year-selector">
                    <button className="year-nav" onClick={() => setYear(y => y - 1)}>←</button>
                    <span className="current-year">{year}</span>
                    <button className="year-nav" onClick={() => setYear(y => y + 1)}>→</button>
                </div>
                <button className="add-vacation-btn" onClick={handleAddClick}>
                    + {t('calendar.add_vacation')}
                </button>
            </div>

            {/* Year Grid (12 months) */}
            {loading ? (
                <div className="calendar-loading">
                    <div className="loading-shimmer-dots"><span></span><span></span><span></span></div>
                </div>
            ) : (
                <>
                    <div className="year-grid">
                        {months.map((monthName, idx) => {
                            const monthVacations = getVacationsForMonth(idx);
                            return (
                                <div key={idx} className="month-card">
                                    <div className="month-name">{monthName}</div>
                                    <div className="month-vacations">
                                        {monthVacations.slice(0, 3).map(v => (
                                            <div
                                                key={v.id}
                                                className={`vacation-chip ${v.type}`}
                                                title={v.title}
                                                onClick={() => handleVacationClick(v)}
                                            >
                                                {v.title.substring(0, 12)}{v.title.length > 12 ? '…' : ''}
                                            </div>
                                        ))}
                                        {monthVacations.length > 3 && (
                                            <div className="vacation-chip more">+{monthVacations.length - 3}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Vacation List */}
                    <div className="vacation-list">
                        <h3>{t('calendar.title')} — {year}</h3>
                        {vacations.length === 0 ? (
                            <div className="empty-state">
                                <p>{t('calendar.empty_state')}</p>
                                <button className="add-first-btn" onClick={handleAddClick}>
                                    + {t('calendar.add_vacation')}
                                </button>
                            </div>
                        ) : (
                            <ul>
                                {vacations.map(v => (
                                    <li key={v.id} className="vacation-item" onClick={() => handleVacationClick(v)}>
                                        <div className="vacation-info">
                                            <strong>{v.title}</strong>
                                            <span className="vacation-dates">
                                                {v.startDate} → {v.endDate}
                                            </span>
                                        </div>
                                        <div className="vacation-meta">
                                            <span className={`type-badge ${v.type}`}>{getTypeLabel(v.type)}</span>
                                            <span className={`status-badge ${v.status}`}>{getStatusLabel(v.status)}</span>
                                        </div>
                                        {v.itinerary && (
                                            <button
                                                className="view-itinerary-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate({ to: '/trip/$slug' as any, params: { slug: v.itinerary!.slug } as any });
                                                }}
                                            >
                                                {t('calendar.view_itinerary')}
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {/* Vacation Modal */}
            {showModal && (
                <VacationModal
                    vacation={editingVacation}
                    defaultValues={defaultValues}
                    onClose={handleModalClose}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
